import React, { useState, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';

import type { InteractiveWidgetProps } from './widgetRegistry';

// ---------------------------------------------------------------------------
// CalculatorWidget
//
// A simple four-function calculator that fits within the slide element's
// bounding box.  All state is transient (React state only, not persisted).
// ---------------------------------------------------------------------------

const BUTTON_ROWS = [
  ['C', '±', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '−'],
  ['1', '2', '3', '+'],
  ['0', '.', '='],
];

const OP_MAP: Record<string, string> = { '÷': '/', '×': '*', '−': '-', '+': '+' };

/** Format a number for display: strip floating-point artefacts, cap length. */
function formatDisplay(n: number): string {
  if (!isFinite(n)) return 'Error';
  // toPrecision(12) eliminates artefacts like 0.30000000000000004
  const s = parseFloat(n.toPrecision(12)).toString();
  return s.length > 14 ? n.toExponential(6) : s;
}

const CalculatorWidget: React.FC<InteractiveWidgetProps> = ({ interactive, width, height }) => {
  const [display, setDisplay] = useState('0');
  const [operand, setOperand] = useState<number | null>(null);
  const [operator, setOperator] = useState<string | null>(null);
  const [resetNext, setResetNext] = useState(false);
  // For repeat-equals: remember the last RHS operand & operator
  const [lastOp, setLastOp] = useState<string | null>(null);
  const [lastRhs, setLastRhs] = useState<number | null>(null);

  const compute = useCallback((a: number, op: string, b: number): number => {
    switch (op) {
      case '+':
        return a + b;
      case '-':
        return a - b;
      case '*':
        return a * b;
      case '/':
        return b !== 0 ? a / b : NaN;
      default:
        return b;
    }
  }, []);

  const handleKey = useCallback(
    (key: string) => {
      if (!interactive) return;

      // Digit or decimal
      if (/^[0-9]$/.test(key) || key === '.') {
        if (key === '.' && !resetNext && display.includes('.')) return;
        const next = resetNext || display === '0' ? (key === '.' ? '0.' : key) : display + key;
        setDisplay(next);
        setResetNext(false);
        return;
      }

      // Clear
      if (key === 'C') {
        setDisplay('0');
        setOperand(null);
        setOperator(null);
        setLastOp(null);
        setLastRhs(null);
        setResetNext(false);
        return;
      }

      // Toggle sign
      if (key === '±') {
        setDisplay((prev) => {
          if (prev === '0' || prev === 'Error') return prev;
          return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
        });
        return;
      }

      // Percentage — context-aware: if mid-operation, treat as % of left operand
      if (key === '%') {
        const current = parseFloat(display);
        if (operand !== null && operator && (operator === '+' || operator === '-')) {
          const pctValue = operand * (current / 100);
          setDisplay(formatDisplay(pctValue));
        } else {
          setDisplay(formatDisplay(current / 100));
        }
        return;
      }

      // Equals
      if (key === '=') {
        const current = parseFloat(display);
        if (operand !== null && operator) {
          // Normal evaluation
          const result = compute(operand, operator, current);
          setDisplay(formatDisplay(result));
          setLastOp(operator);
          setLastRhs(current);
          setOperand(null);
          setOperator(null);
          setResetNext(true);
        } else if (lastOp !== null && lastRhs !== null) {
          // Repeat-equals: re-apply last operation
          const result = compute(current, lastOp, lastRhs);
          setDisplay(formatDisplay(result));
          setResetNext(true);
        }
        return;
      }

      // Operator
      const op = OP_MAP[key];
      if (op) {
        const current = parseFloat(display);
        if (operand !== null && operator && !resetNext) {
          // Chain: evaluate pending operation first
          const result = compute(operand, operator, current);
          setDisplay(formatDisplay(result));
          setOperand(isFinite(result) ? result : null);
        } else {
          setOperand(current);
        }
        setOperator(op);
        setLastOp(null);
        setLastRhs(null);
        setResetNext(true);
      }
    },
    [interactive, display, operand, operator, resetNext, lastOp, lastRhs, compute]
  );

  // Scale font sizes relative to element dimensions
  const baseFontSize = Math.max(10, Math.min(width, height) * 0.07);
  const displayFontSize = baseFontSize * 1.6;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1e1e1e',
        borderRadius: 1,
        overflow: 'hidden',
        userSelect: 'none',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      // Stop clicks from bubbling to the slide element selection handler
      onMouseDown={interactive ? (e) => e.stopPropagation() : undefined}
    >
      {/* Display */}
      <Box
        sx={{
          flex: '0 0 auto',
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          minHeight: height * 0.18,
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            fontSize: displayFontSize,
            fontFamily: 'monospace',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {display}
        </Typography>
      </Box>

      {/* Button grid — 4-column CSS grid so the wide "0" key aligns with columns above */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridAutoRows: '1fr',
          gap: '1px',
          p: '1px',
        }}
      >
        {BUTTON_ROWS.flat().map((key) => {
          const isOp = ['÷', '×', '−', '+', '='].includes(key);
          const isFunc = ['C', '±', '%'].includes(key);
          const isZero = key === '0';

          return (
            <Button
              key={key}
              onClick={() => handleKey(key)}
              tabIndex={interactive ? 0 : -1}
              sx={{
                gridColumn: isZero ? 'span 2' : undefined,
                minWidth: 0,
                borderRadius: 0,
                fontSize: baseFontSize,
                fontWeight: 500,
                color: isOp ? '#fff' : isFunc ? '#000' : '#fff',
                bgcolor: isOp ? '#f09a36' : isFunc ? '#a5a5a5' : '#333',
                '&:hover': {
                  bgcolor: isOp ? '#d4882e' : isFunc ? '#8e8e8e' : '#4a4a4a',
                },
              }}
            >
              {key}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default React.memo(CalculatorWidget);
